import * as React from 'react';

const STORAGE_KEY = 'mcs_cookie_consent';
const CONSENT_VERSION = 1;

export type CookieCategory = 'essential' | 'analytics' | 'marketing';

export interface CookieConsent {
  timestamp: string;
  version: number;
  categories: Record<CookieCategory, boolean>;
}

const DEFAULT_CONSENT: Record<CookieCategory, boolean> = {
  essential: true,
  analytics: false,
  marketing: false,
};

function readStoredConsent(): CookieConsent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent | null;
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(categories: Record<CookieCategory, boolean>): CookieConsent {
  const consent: CookieConsent = {
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
    categories: { ...DEFAULT_CONSENT, ...categories, essential: true },
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // localStorage blocked; banner will re-show next visit. Acceptable.
  }
  return consent;
}

export interface CookieBannerProps {
  /**
   * Optional remote-persistence hook. Called with the consent record after
   * the user accepts/rejects. Failures are swallowed; localStorage is the
   * source of truth.
   */
  onConsent?: (consent: CookieConsent) => void | Promise<void>;
  /** Localised strings. All required so the banner has no English fallback. */
  texts: {
    title: string;
    body: string;
    acceptAll: string;
    rejectNonEssential: string;
    customize: string;
    save: string;
    essentialLabel: string;
    essentialDesc: string;
    analyticsLabel: string;
    analyticsDesc: string;
    marketingLabel: string;
    marketingDesc: string;
  };
}

export function CookieBanner({ onConsent, texts }: CookieBannerProps): React.ReactElement | null {
  const [visible, setVisible] = React.useState(() => readStoredConsent() === null);
  const [customizeOpen, setCustomizeOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<Record<CookieCategory, boolean>>(DEFAULT_CONSENT);

  const persist = React.useCallback(
    (categories: Record<CookieCategory, boolean>) => {
      const consent = writeConsent(categories);
      setVisible(false);
      void Promise.resolve(onConsent?.(consent)).catch(() => {
        // ignore; localStorage already saved.
      });
    },
    [onConsent],
  );

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label={texts.title}
      aria-modal="false"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-[var(--color-border)] bg-[var(--color-bg)] shadow-lg"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-4 p-4 md:p-6">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold">{texts.title}</h2>
          <p className="text-sm text-[var(--color-fg-secondary)]">{texts.body}</p>
        </div>

        {customizeOpen ? (
          <div className="flex flex-col gap-3 rounded-md border border-[var(--color-border)] p-3">
            <CategoryRow
              label={texts.essentialLabel}
              description={texts.essentialDesc}
              checked={true}
              disabled
              onChange={() => undefined}
            />
            <CategoryRow
              label={texts.analyticsLabel}
              description={texts.analyticsDesc}
              checked={draft.analytics}
              onChange={(v) => setDraft((d) => ({ ...d, analytics: v }))}
            />
            <CategoryRow
              label={texts.marketingLabel}
              description={texts.marketingDesc}
              checked={draft.marketing}
              onChange={(v) => setDraft((d) => ({ ...d, marketing: v }))}
            />
          </div>
        ) : null}

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          {!customizeOpen ? (
            <button
              type="button"
              onClick={() => setCustomizeOpen(true)}
              className="inline-flex items-center justify-center rounded-md border border-[var(--color-border)] px-4 py-2 text-sm font-medium hover:bg-[var(--color-bg-hover)]"
            >
              {texts.customize}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => persist({ essential: true, analytics: false, marketing: false })}
            className="inline-flex items-center justify-center rounded-md border border-[var(--color-border)] px-4 py-2 text-sm font-medium hover:bg-[var(--color-bg-hover)]"
          >
            {texts.rejectNonEssential}
          </button>
          {customizeOpen ? (
            <button
              type="button"
              onClick={() => persist(draft)}
              className="inline-flex items-center justify-center rounded-md bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-[var(--color-accent-fg)] hover:opacity-90"
            >
              {texts.save}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => persist({ essential: true, analytics: true, marketing: true })}
              className="inline-flex items-center justify-center rounded-md bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-[var(--color-accent-fg)] hover:opacity-90"
            >
              {texts.acceptAll}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryRow({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 text-sm">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-[var(--color-border)] accent-[var(--color-accent)]"
      />
      <span className="flex flex-col gap-0.5">
        <span className="font-medium">{label}</span>
        <span className="text-[var(--color-fg-secondary)]">{description}</span>
      </span>
    </label>
  );
}
